import { useEffect, useMemo, useState } from "react";
import { Drawer } from "./Drawer";
import { fetchAiSuggestionReviewActivity } from "../../services/recommendationActivityService";
import type {
  ContractSummaryForActivity,
  RecommendationActivityItem
} from "../../types/recommendationActivity";

type ActivityStreamDrawerProps = {
  readonly isOpen: boolean;
  readonly generatedContractId: string | null;
  readonly onClose: () => void;
};

type GroupMeta = {
  readonly label: string;
  readonly dotClass: string;
  readonly sort: number;
};

function formatIsoDateYmd(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function classifyActivityStatus(status: string): GroupMeta {
  const s = status.trim().toLowerCase();
  if (
    s.includes("high") ||
    s === "legal" ||
    s === "flag" ||
    s === "critical"
  ) {
    return { label: "High Risk", dotClass: "bg-red-500", sort: 0 };
  }
  if (
    s.includes("medium") ||
    s === "amber" ||
    s === "business" ||
    s === "warn"
  ) {
    return { label: "Medium Risk", dotClass: "bg-amber-500", sort: 1 };
  }
  if (s === "pending") {
    return { label: "Pending", dotClass: "bg-amber-400", sort: 2 };
  }
  if (s === "approved") {
    return { label: "Approved", dotClass: "bg-emerald-500", sort: 3 };
  }
  if (s === "rejected") {
    return { label: "Rejected", dotClass: "bg-red-600", sort: 4 };
  }
  const raw = status.replace(/_/g, " ").trim();
  const label =
    raw.length > 0
      ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
      : "Other";
  return { label, dotClass: "bg-gray-400", sort: 5 };
}

function primaryActivityLine(row: RecommendationActivityItem): string {
  const pick =
    row.ai_suggested_text?.trim() ||
    row.client_suggested_text?.trim() ||
    row.approved_text?.trim() ||
    row.original_text?.trim() ||
    "";
  return pick || "—";
}

function contractStatusBadgeClasses(status: string): string {
  const s = status.trim().toLowerCase();
  if (
    s === "completed" ||
    s === "review_completed" ||
    s === "finalized" ||
    s === "generated"
  ) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (s === "draft" || s === "in_review" || s.includes("pending")) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (s === "processing") {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-gray-200 bg-gray-50 text-gray-700";
}

function formatContractStatusLabel(status: string): string {
  const s = status.trim();
  if (!s) return "—";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const ActivityStreamDrawer = ({
  isOpen,
  generatedContractId,
  onClose
}: ActivityStreamDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<ContractSummaryForActivity | null>(
    null
  );
  const [items, setItems] = useState<RecommendationActivityItem[]>([]);

  useEffect(() => {
    if (!isOpen || !generatedContractId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContract(null);
    setItems([]);

    void (async () => {
      try {
        const res = await fetchAiSuggestionReviewActivity(generatedContractId, {
          page: 1,
          pageSize: 20,
          mine: false
        });
        if (cancelled) return;
        setContract(res.contract);
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load activity.");
        setContract(null);
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, generatedContractId]);

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { meta: GroupMeta; rows: RecommendationActivityItem[] }
    >();
    for (const row of items) {
      const meta = classifyActivityStatus(row.status);
      const existing = map.get(meta.label);
      if (existing) {
        existing.rows.push(row);
      } else {
        map.set(meta.label, { meta, rows: [row] });
      }
    }
    return [...map.values()].sort((a, b) => a.meta.sort - b.meta.sort);
  }, [items]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Activity Stream"
      subtitle="Contract activity and recommendations"
      panelClassName="max-w-xl"
      className="px-6 py-5"
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500">
          <svg
            className="h-8 w-8 animate-spin text-primary-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p>Loading activity…</p>
        </div>
      ) : error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          {error}
        </div>
      ) : (
        <>
          {contract ? (
            <section className="mb-6 rounded-xl border border-gray-100 bg-gray-50/80 p-4">
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Contract Name
                  </dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {contract.contract_name?.trim() || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Contract Type
                  </dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {contract.contract_type?.trim() || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </dt>
                  <dd className="mt-0.5">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${contractStatusBadgeClasses(contract.contract_status)}`}
                    >
                      {formatContractStatusLabel(contract.contract_status)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Created by
                  </dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {contract.created_by_user?.full_name?.trim() || "—"}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Created on
                  </dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {formatIsoDateYmd(contract.created_on)}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}

          <h3 className="mb-3 text-sm font-semibold text-gray-900">
            Recommendation Activity
          </h3>

          {!loading && !error && items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
              No recommendation activity for this contract yet.
            </p>
          ) : null}

          <div className="space-y-6">
            {grouped.map(({ meta, rows }) => (
              <div key={meta.label}>
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${meta.dotClass}`}
                    aria-hidden
                  />
                  <span className="text-sm font-semibold text-gray-800">
                    {meta.label} — {rows.length}{" "}
                    {rows.length === 1 ? "entry" : "entries"}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                  {rows.map((row) => (
                    <li key={row.event_id} className="px-3 py-3 first:pt-3 last:pb-3">
                      <div className="flex gap-3">
                        <span
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dotClass}`}
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <p
                              className="min-w-0 flex-1 truncate text-sm text-gray-900"
                              title={primaryActivityLine(row)}
                            >
                              {primaryActivityLine(row)}
                            </p>
                            <time
                              className="shrink-0 text-xs text-gray-500"
                              dateTime={row.activity_created_at}
                            >
                              {formatIsoDateYmd(row.activity_created_at)}
                            </time>
                          </div>
                          <div className="mt-2 grid gap-1 text-xs text-gray-600 sm:grid-cols-2">
                            <p>
                              <span className="font-medium text-gray-500">
                                Created by:{" "}
                              </span>
                              {contract?.created_by_user?.full_name?.trim() || "—"}
                            </p>
                            <p>
                              <span className="font-medium text-gray-500">
                                Reviewed by:{" "}
                              </span>
                              {row.reviewer?.full_name?.trim() || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
};
